import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import ScoresTicker from "@/components/ScoresTicker";
import AthleteCard from "@/components/AthleteCard";
import CausesSection from "@/components/CausesSection";
import HowToBack from "@/components/HowToBack";
import FeaturedStory from "@/components/FeaturedStory";
import PartnerCTA from "@/components/PartnerCTA";
import ShopSection from "@/components/ShopSection";
import Footer from "@/components/Footer";
import { athletes as seedAthletes } from "@/data/athletes";
import { publicUrl } from "@/lib/storage";
import waveLogo from "@/assets/jersey-shore-wave-logo.png";

import { athleteImageUrl } from "@/lib/athleteImage";
import type { Athlete } from "@/data/athletes";
import { useSiteContent } from "@/hooks/useSiteContent";
import { useAuth } from "@/hooks/useAuth";
import { useFavorites } from "@/hooks/useFavorites";
import type { AthleteLinks } from "@/components/AthleteSocialLinks";

const HOMEPAGE_ATHLETE_LIMIT = 10;

const Index = () => {
  const [filter, setFilter] = useState("All");
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [loading, setLoading] = useState(true);
  // Map slug -> DB uuid (only present for db-loaded athletes, not seeds).
  const [dbIds, setDbIds] = useState<Record<string, string>>({});
  const [linksBySlug, setLinksBySlug] = useState<Record<string, AthleteLinks>>({});
  const [usingSeed, setUsingSeed] = useState(false);
  const { t } = useSiteContent();
  const { user } = useAuth();
  const { favorites } = useFavorites();

  useEffect(() => {
    (async () => {
      const [aRes, cRes, pRes, caRes, paRes] = await Promise.all([
        supabase.from("athletes").select("*").eq("published", true).order("sort_order"),
        supabase.from("causes").select("*").eq("published", true).order("sort_order"),
        supabase.from("brand_products").select("*").eq("published", true).order("sort_order"),
        supabase.from("cause_athletes").select("cause_id, athlete_id"),
        supabase.from("product_athletes").select("product_id, athlete_id"),
      ]);
      const dbAthletes = aRes.data ?? [];
      if (dbAthletes.length === 0) {
        setAthletes(seedAthletes);
        setUsingSeed(true);
        setLoading(false);
        return;
      }
      const causes = cRes.data ?? [];
      const products = pRes.data ?? [];
      const causeLinks: Record<string, string[]> = {};
      ((caRes.data as any) ?? []).forEach((r: any) => { (causeLinks[r.cause_id] ||= []).push(r.athlete_id); });
      const productLinks: Record<string, string[]> = {};
      ((paRes.data as any) ?? []).forEach((r: any) => { (productLinks[r.product_id] ||= []).push(r.athlete_id); });

      const athleteRefById: Record<string, { id: string; name: string }> = {};
      dbAthletes.forEach((a: any) => { athleteRefById[a.id] = { id: a.id, name: a.name }; });

      const idMap: Record<string, string> = {};
      const linkMap: Record<string, AthleteLinks> = {};
      const merged: Athlete[] = dbAthletes.map((a: any) => {
        idMap[a.slug] = a.id;
        linkMap[a.slug] = {
          instagram_url: a.instagram_url,
          tiktok_url: a.tiktok_url,
          facebook_url: a.facebook_url,
          youtube_url: a.youtube_url,
          linkedin_url: a.linkedin_url,
          custom_links: Array.isArray(a.custom_links) ? a.custom_links : [],
        };
        const myCauses = causes.filter((c: any) => (causeLinks[c.id] ?? (c.athlete_id ? [c.athlete_id] : [])).includes(a.id));
        const myProducts = products.filter((p: any) => (productLinks[p.id] ?? (p.athlete_id ? [p.athlete_id] : [])).includes(a.id));
        return {
          id: a.slug,
          name: a.name,
          sport: a.sport,
          country: a.country ?? "",
          image: athleteImageUrl(a.image_path),
          tagline: a.tagline ?? "",
          bio: a.bio ?? "",
          stats: Array.isArray(a.stats) ? a.stats : [],
          causes: myCauses.map((c: any) => ({
            id: c.id,
            name: c.name,
            blurb: c.blurb ?? "",
            raised: `$${Number(c.raised).toLocaleString()}`,
            goal: `$${Number(c.goal).toLocaleString()}`,
            donateUrl: c.donate_url ?? null,
            logoUrl: publicUrl(c.logo_path),
            galleryUrls: Array.isArray(c.image_paths) ? (c.image_paths.map((ip: string) => publicUrl(ip)).filter(Boolean) as string[]) : [],
            athletes: (causeLinks[c.id] ?? (c.athlete_id ? [c.athlete_id] : [])).map((id: string) => athleteRefById[id]).filter(Boolean),
          })),
          brands: myProducts.map((p: any) => ({
            id: p.id,
            name: p.brand_name,
            category: p.category ?? "",
            product: p.product_name,
            price: p.price ?? "",
            productUrl: p.product_url ?? null,
            quote: p.quote ?? null,
            imageUrl: publicUrl(p.image_path),
            galleryUrls: Array.isArray(p.image_paths) ? (p.image_paths.map((ip: string) => publicUrl(ip)).filter(Boolean) as string[]) : [],
            isHouseBrand: !!p.is_house_brand,
            athletes: (productLinks[p.id] ?? (p.athlete_id ? [p.athlete_id] : [])).map((id: string) => athleteRefById[id]).filter(Boolean),
          })),
        };
      });
      setAthletes(merged);
      setDbIds(idMap);
      setLinksBySlug(linkMap);
      setUsingSeed(false);
      setLoading(false);
    })();
  }, []);

  const sports = ["All", ...Array.from(new Set(athletes.map((a) => a.sport)))];
  const myPlayersAvailable = user && Object.keys(dbIds).length > 0;
  const filterChips = myPlayersAvailable ? ["My Players", ...sports] : sports;

  const filtered =
    filter === "My Players"
      ? athletes.filter((a) => {
          const id = dbIds[a.id];
          return id && favorites.has(id);
        })
      : filter === "All"
        ? athletes
        : athletes.filter((a) => a.sport === filter);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main>
        <Hero />
        <ScoresTicker />
        <HowToBack />

        {/* Campaign CTA — Know Her Name. Back Her Game. */}
        <section className="bg-wave text-wave-foreground">
          <div className="container py-8 md:py-10 flex flex-col md:flex-row md:items-center gap-5 md:gap-8">
            <img
              src={waveLogo}
              alt="Jersey Shore Wave"
              width={160}
              height={80}
              loading="lazy"
              className="h-10 md:h-12 w-auto bg-wave-foreground rounded-xl p-1.5 shrink-0 self-start"
            />
            <div className="grow">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] opacity-80">
                Say It In Sports × Jersey Shore Wave
              </p>
              <h2 className="font-display text-2xl md:text-3xl mt-1">Know her name. Back her game.</h2>
            </div>
            <Link
              to="/wave"
              className="inline-flex items-center justify-center gap-2 min-h-[44px] px-6 rounded-full bg-wave-foreground text-wave font-semibold shrink-0 hover:bg-primary hover:text-primary-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-wave"
            >
              See the campaign →
            </Link>
          </div>
        </section>


        <section id="athletes" className="py-14 md:py-20">
          <div className="container">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
              <div className="max-w-2xl">
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
                  {t("athletes.eyebrow", "The roster")}
                </span>
                <h2 className="text-5xl md:text-7xl mt-3 text-balance">
                  {t("athletes.heading", "Meet the Jersey Shore Wave")}
                </h2>
                <p className="text-muted-foreground mt-3 max-w-md">
                  {t("athletes.intro", "Tap a card to read her story, support her cause, or shop her partners.")}
                </p>
                {usingSeed && (
                  <p className="text-xs text-muted-foreground mt-3">
                    Showing demo content. Sign in to <a href="/admin" className="text-primary underline">/admin</a> to publish your own.
                  </p>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {filterChips.map((s) => (
                  <button
                    key={s}
                    onClick={() => setFilter(s)}
                    className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                      filter === s
                        ? "bg-secondary text-secondary-foreground border-secondary"
                        : "bg-background text-foreground border-border hover:border-secondary"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {loading
                ? Array.from({ length: HOMEPAGE_ATHLETE_LIMIT }).map((_, i) => (
                    <div key={i} className="rounded-2xl overflow-hidden bg-card shadow-card">
                      <div className="aspect-[3/4] bg-muted animate-pulse" />
                      <div className="p-3 space-y-2">
                        <div className="h-4 bg-muted rounded animate-pulse" />
                        <div className="h-3 bg-muted rounded w-2/3 animate-pulse" />
                      </div>
                    </div>
                  ))
                : filtered.slice(0, HOMEPAGE_ATHLETE_LIMIT).map((a) => (
                    <AthleteCard
                      key={a.id}
                      athlete={a}
                      favoriteId={dbIds[a.id]}
                      hasProfile={!usingSeed && !!dbIds[a.id]}
                      links={linksBySlug[a.id]}
                    />
                  ))}
            </div>
            {filter === "My Players" && filtered.length === 0 && (
              <p className="text-center text-muted-foreground py-12">
                You haven't favorited any players yet. Tap the heart on a card to follow them.
              </p>
            )}
            <div className="flex justify-center mt-12">
              <Link
                to="/athletes"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-secondary text-secondary-foreground text-sm font-semibold hover:bg-secondary/90 transition-colors"
              >
                Meet all athletes →
              </Link>
            </div>
          </div>
        </section>

        <FeaturedStory
          athletes={athletes}
          hasProfile={(slug) => !usingSeed && !!dbIds[slug]}
        />
        <CausesSection athletes={athletes} />
        <ShopSection athletes={athletes} />
        <PartnerCTA />
      </main>
      <Footer />
    </div>
  );
};

export default Index;
